import { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';

import OptionalComponent from '@ui/optional-component/optional-component';
import Portal from '@ui/portal/portal';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { MODAL_ANIMATION_TIME, ModalProps } from './types';
import randomString from '@/utils/random-string';
import { isDefined } from '@/utils/is-defined';

import styles from './modal.module.css';

const Modal = memo(
  ({
    id,
    open,
    contentWrapperClassName,
    contentClassName,
    portalClassName,
    modalClassName,
    footerClassName,
    backgroundColor,
    onRequestClose,
    animation = 'slide-up',
    size = 'medium',
    withZoom = true,
    showBackdrop = true,
    children,
    renderHeaderContent,
    renderFooterContent,
    customSize,
    delayContentRender = 0,
    showLoadingIndicator = false,
    delayRenderLoadingIndicator = 0,
    onTransitionOpening,
    onTransitionOpened,
    onTransitionClosing,
    onTransitionClosed,
    fadeTransition = true,
    transitionDuration = MODAL_ANIMATION_TIME,
    transitionTimingFunction = 'ease-in-out',
    animateHeightTransition,
    'data-component': dataComponent = 'Modal',
    drawerMode,
  }: ModalProps) => {
    const [showModal, setShowModal] = useState(open);
    const [closing, setClosing] = useState(false);
    // There's a scenario were onTransitionOpened is not called when the modal is opened by default on first render,
    // this is a workaround to ensure that onTransitionOpened is in that scenario.
    // Do not update this value anywhere else in the component. It's a set and forget value.
    const isDefaultOpen = useRef(open);
    const hasOpenedAtLeastOnceRef = useRef(false);

    const portalRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const closeTimeout = useRef<NodeJS.Timeout | null>(null);
    const openedCheckTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      const portalEl = portalRef.current;
      if (!portalEl) return;

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }

      if (open) {
        // timeout required on setShowModal because transition methods make first render lose their animations.
        setTimeout(() => setShowModal(true));
        portalEl.classList.add(`${[styles['open']]}`);
        onTransitionOpening?.();
        // if the modal is opened by default, we need to call onTransitionOpened
        if (isDefaultOpen.current && !hasOpenedAtLeastOnceRef.current) {
          onTransitionOpened?.();
        }
        hasOpenedAtLeastOnceRef.current = true;
      } else {
        if (hasOpenedAtLeastOnceRef.current) {
          onTransitionClosing?.();
          setClosing(true);
        }
        closeTimeout.current = setTimeout(() => {
          setShowModal(false);
          portalEl.classList.remove(`${[styles['open']]}`);
          if (hasOpenedAtLeastOnceRef.current) {
            onTransitionClosed?.();
            setClosing(false);
          }
        }, transitionDuration);
      }

      return () => {
        if (closeTimeout.current) {
          clearTimeout(closeTimeout.current);
        }
      };
      // only run this effect when the open prop changes, all the transition methods will be called at that time anyway
      // and we also don't want to run this effect at any other time than when the open prop changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // call onTransitionOpened after opened transition completes (do not use ontransitionend event listener, it's too inconsistent)
    useEffect(() => {
      if (openedCheckTimer.current) {
        clearTimeout(openedCheckTimer.current);
      }
      if (!open || !showModal) return;

      openedCheckTimer.current = setTimeout(() => {
        onTransitionOpened?.();
      }, transitionDuration);

      return () => {
        if (openedCheckTimer.current) {
          clearTimeout(openedCheckTimer.current);
        }
      };
    }, [onTransitionOpened, open, showModal, transitionDuration]);

    useEffect(() => {
      if (!onRequestClose) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (!open) return;
          e.stopPropagation(); // prevent other escape key events from being triggered
          onRequestClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onRequestClose, open]);

    useEffect(() => {
      // This effect is used to set the data-component attribute on the portal element
      // This is useful for testing purposes
      const portalEl = portalRef.current;
      if (portalEl) {
        portalEl.setAttribute('data-component', dataComponent);
      }
    }, [dataComponent]);

    return (
      <Portal
        id={id || randomString(7)}
        className={classNames(styles['modal-portal'], {
          [portalClassName || '']: !!portalClassName,
        })}
        aria-hidden={!open && !showModal}
        ref={portalRef}
      >
        <FocusLock
          returnFocus
          disabled={!open && !showModal} /* without this focus trap doesn't work */
          className={classNames(
            'flex-column jc-center ai-center',
            styles['modal-focus-lock'],
            styles['size-' + (size || 'medium')],
            {
              [styles['open']]: open,
              [styles['show']]: showModal || closing,
            }
          )}
        >
          <OptionalComponent display={showBackdrop}>
            <div
              role="presentation"
              className={classNames(styles['modal-backdrop'], { [styles.transparent]: drawerMode })}
              onClick={onRequestClose}
            />
          </OptionalComponent>
          <div
            role="dialog"
            aria-modal="true"
            inert={!open && !closing}
            ref={bodyRef}
            className={classNames(
              'global-modal-body',
              styles['modal-body'],
              styles[animation || 'slide-up'],
              styles['size-' + (size || 'medium')],
              {
                [styles['zoom']]: withZoom,
                [styles['custom-size']]: !!customSize && Object.keys(customSize).length > 0,
                [styles['animate-height-transition']]: animateHeightTransition,
                [styles.drawer]: drawerMode,
              },
              modalClassName
            )}
            style={
              {
                ...(isDefined(customSize?.width) ? { '--modalBodyCustomWidth': customSize.width } : {}),
                ...(isDefined(customSize?.maxWidth) ? { '--modalBodyCustomMaxWidth': customSize.maxWidth } : {}),
                ...(isDefined(customSize?.height) ? { '--modalBodyCustomHeight': customSize.height } : {}),
                ...(isDefined(customSize?.maxHeight) ? { '--modalBodyCustomMaxHeight': customSize.maxHeight } : {}),
                ...(isDefined(customSize?.minHeight) ? { '--modalBodyCustomMinHeight': customSize.minHeight } : {}),
                ...(isDefined(customSize?.minWidth) ? { '--modalBodyCustomMinWidth': customSize.minWidth } : {}),
                ...(backgroundColor ? { backgroundColor } : {}),
                '--modalInitialOpacity': fadeTransition ? 0 : 1,
                '--modaltransitionDuration': `${transitionDuration ?? 300}ms`,
                '--modalTransitionTimingFunction': transitionTimingFunction ?? 'ease-in-out',
              } as React.CSSProperties
            }
          >
            <OptionalComponent delayRender={delayRenderLoadingIndicator} display={open && showLoadingIndicator}>
              <div className="flex-column jc-center ai-center height-100-percent">
                <WaitingIndicator />
              </div>
            </OptionalComponent>
            {/* intentionally unmounting content when modal is closed so that content is forced to re-render when re-opening */}
            {/* delayContentRender is used to prevent jank when async calls render before the animation has complete */}
            <OptionalComponent delayRender={delayContentRender} display={!showLoadingIndicator && (showModal || open)}>
              <div
                className={classNames('flex-column', 'height-100-percent', styles['modal-content-wrapper'], {
                  [contentWrapperClassName || '']: !!contentWrapperClassName,
                })}
              >
                <OptionalComponent display={!!renderHeaderContent}>
                  <div className={styles['modal-header']}>{renderHeaderContent?.()}</div>
                </OptionalComponent>

                <div className={`${contentClassName || ''} ${styles['modal-content']}`}>{children}</div>

                <OptionalComponent display={!!renderFooterContent}>
                  <div className={footerClassName || ''}>{renderFooterContent?.()}</div>
                </OptionalComponent>
              </div>
            </OptionalComponent>
          </div>
        </FocusLock>
      </Portal>
    );
  }
);

export default Modal;
